// HubApp.js — Coursera ABM Resource Hub (content only; header lives in App.js)

import React, { useEffect, useMemo, useState } from "react";

/* =========================
   1) Configuration
========================= */

const DATA_URL = ""; // leave blank to use SAMPLE below
const RESOURCES_URL = "/abm-resources.json";

// Logos & Conduit assets
const LOGO_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";
const CONDUIT_RGB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/conduit/RGB_Conduit.svg";
const CONDUIT_60 =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/conduit/Size%3D60%25%2C%20Type%3DColor%20Fill.svg";
const CONDUIT_35 =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/conduit/Size%3D35%25%2C%20Type%3DColor%20Fill.svg";

/* Demo data (auto-linked to PDFs / videos via map below) */
const SAMPLE = {
  items: [
    /* -------- MARKETING: PLAYBOOK -------- */
    {
      id: "coursera-abm-reporting",
      slug: "abm-marketing-and-sales-101",
      title: "ABM Marketing and Sales 101",
      summary:
        "Clear ABM 1:1 and 1:Few playbook so Marketing, SDRs, and Sales share metrics, stages, and moves for priority accounts.",
      industries: ["Education", "Software"],
      personas: [
        "Marketing Leadership",
        "Demand Gen",
        "Marketing Ops",
        "RevOps",
      ],
      topics: [
        "ABM 1:1 & 1:Few",
        "Metrics & Scorecard",
        "Stages / Journey",
        "Buying Committee",
        "Reporting",
      ],
      tags: ["Marketing", "ABM Basics", "Measurement", "Dashboards"],
      funnel_stage: "Awareness",
      release_date: "2025-11-01",
      version: 1,
      read_time_min: 12,
      words: 2400,
      content_type: "playbook",
      file_url: "#/pdf/marketing",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abm_101.png",
    },

    /* -------- MARKETING: VIDEO WALKTHROUGH -------- */
    {
      id: "coursera-abm-video",
      slug: "abm-marketing-and-sales-101-video",
      title: "ABM Marketing & Sales 101 — Video Walkthrough",
      summary:
        "Video walkthrough of the ABM Marketing & Sales 101 guide: how to read the scorecard, stages, and ABM programs.",
      industries: ["Education", "Software"],
      personas: [
        "Marketing Leadership",
        "Demand Gen",
        "Marketing Ops",
        "RevOps",
      ],
      topics: [
        "ABM 1:1 & 1:Few",
        "Metrics & Scorecard",
        "Stages / Journey",
        "Buying Committee",
        "Reporting",
      ],
      tags: ["Marketing", "ABM Basics", "Measurement", "Dashboards", "Video"],
      funnel_stage: "Awareness",
      release_date: "2025-11-01",
      version: 1,
      duration_sec: 840, // ~14 min
      content_type: "video",
      file_url:
        "https://assets.fairwaydigitalmedia.com/coursera/videos/abm_101.mp4",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abm_101.png",
    },

    /* -------- SDR: PLAYBOOK -------- */
    {
      id: "coursera-tal-how-to",
      slug: "how-to-work-a-target-account-list",
      title: 'SDR + AE + ABM — "Teamwork Makes The ABM Dreamwork"',
      summary:
        "ABM guide for SDRs: how to use signals, content, and AE/Marketing support to work named accounts with confidence.",
      industries: ["Education", "Software"],
      personas: ["SDR", "BDR", "Inside Sales", "Sales Development Leadership"],
      topics: [
        "Target Account List (TAL)",
        "Signals & Intent",
        "Sequences by Stage",
        "Buying Committee",
        "ABM + SDR Collaboration",
      ],
      tags: ["SDR Best Practices", "Outbound", "Sequences", "ABM Enablement"],
      funnel_stage: "Consideration",
      release_date: "2025-11-01",
      version: 1,
      duration_sec: 900,
      read_time_min: 8,
      content_type: "playbook",
      file_url: "#/pdf/sdr",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsdr_101.png",
    },

    /* -------- SDR: VIDEO WALKTHROUGH -------- */
    {
      id: "coursera-sdr-video",
      slug: "sdr-video-teamwork-abm-dreamwork",
      title: 'SDR Video — "Teamwork Makes The ABM Dreamwork"',
      summary:
        "6-minute SDR walkthrough on TALs, signals, and how to book better meetings with ABM support.",
      industries: ["Education", "Software"],
      personas: ["SDR", "BDR", "Inside Sales", "Sales Development Leadership"],
      topics: [
        "Target Account List (TAL)",
        "Signals & Intent",
        "Sequences by Stage",
        "ABM + SDR Collaboration",
      ],
      tags: ["SDR Best Practices", "Outbound", "Sequences", "ABM Enablement", "Video"],
      funnel_stage: "Consideration",
      release_date: "2025-11-01",
      version: 1,
      duration_sec: 360, // ~6 min
      content_type: "video",
      file_url:
        "https://assets.fairwaydigitalmedia.com/coursera/videos/coursera_abm_sdr.mp4",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsdr_101.png",
    },

    /* -------- SALES: PLAYBOOK -------- */
    {
      id: "coursera-account-plan",
      slug: "account-plan-example-best-practices",
      title: 'Sales Deliverable — "Account Plan Example & Best Practices"',
      summary:
        "Simple ABM account-plan example that shows reps how to map the buying group and run coordinated plays with SDRs and Marketing.",
      industries: ["Education", "Software"],
      personas: ["Account Executive", "Sales Leadership", "Sales Ops"],
      topics: [
        "Account Planning",
        "ABM 1:1 Plans",
        "1:Few Motions",
        "Buying Committee Mapping",
        "Rules of Engagement",
        "AE–SDR–Marketing Rhythm",
      ],
      tags: ["Sales Enablement", "Best Practices", "Playbook", "Deal Strategy"],
      funnel_stage: "Decision",
      release_date: "2025-11-01",
      version: 1,
      read_time_min: 10,
      words: 2000,
      content_type: "playbook",
      file_url: "#/pdf/sales",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsales_101.png",
    },

    /* -------- SALES: VIDEO WALKTHROUGH -------- */
    {
      id: "coursera-account-plan-video",
      slug: "account-plan-example-best-practices-video",
      title: "Account Plan Example — Video Walkthrough",
      summary:
        "Video walkthrough of the Account Plan Example & Best Practices deliverable and how to use it with SDRs and Marketing.",
      industries: ["Education", "Software"],
      personas: ["Account Executive", "Sales Leadership", "Sales Ops"],
      topics: [
        "Account Planning",
        "ABM 1:1 Plans",
        "1:Few Motions",
        "Buying Committee Mapping",
        "Rules of Engagement",
        "AE–SDR–Marketing Rhythm",
      ],
      tags: ["Sales Enablement", "Best Practices", "Deal Strategy", "Video"],
      funnel_stage: "Decision",
      release_date: "2025-11-01",
      version: 1,
      duration_sec: 600, // ~10 min
      content_type: "video",
      file_url:
        "https://assets.fairwaydigitalmedia.com/coursera/videos/sales_accountplans.mp4",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abmsales_101.png",
    },

    /* -------- TOOL: SOCIAL COMMENTS -------- */
    {
      id: "coursera-social-comments",
      slug: "social-comments-simple-cut-and-paste",
      title: "ABM Tool — Social Comments: Simple Cut & Paste",
      summary:
        "Turn social “reactions” into a clean contacts table: name, title, company, connection degree, plus LinkedIn handle/URL capture. CSV/XLSX export, privacy-safe.",
      industries: ["Education", "Software"],
      personas: [
        "Marketing Ops",
        "Demand Gen",
        "ABM Marketing",
        "SDR Leadership",
        "RevOps",
      ],
      topics: [
        "Social Listening → Contacts",
        "LinkedIn Handle & URL Capture",
        "Signals to Outreach",
        "CSV/XLSX Export",
        "PII-Safe Workflows",
      ],
      tags: [
        "ABM Tools",
        "Workflow Automation",
        "Privacy-First",
        "Fairway Powered",
      ],
      funnel_stage: "Enablement",
      release_date: "2025-11-14",
      version: 1,
      read_time_min: 6,
      words: 1200,
      content_type: "tool",
      // PDF one-pager/guide
      file_url: "#/pdf/tool-social",
      // Live tool launcher
      tool_url: "#/tools/social-comments",
      cover_url:
        "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/sdrs_clipboard.png",
    },
  ],
};

/* =========================
   Default role tools (fallback if RESOURCES_URL missing)
   UPDATED WITH FORRESTER LINK
========================= */

const DEFAULT_TOOLS = {
  marketing: [
    {
      title: "Forrester Call Prep",
      desc: "Interactive guide: Coursera questions + Fairway context lenses.",
      href: "https://forrestercallprep.fairwaydigitalmedia.com/",
    },
    {
      title: "FS AI Personas (Interactive)",
      desc: "Deep dive into L&D, IT, and Exec challenges in the AI era.",
      href: "#/tools/fs-persona-carousel",
    },
    {
      title: "Tech Sector Personas", 
      desc: "Executive briefing lens for Engineering, Product, and HR.",
      href: "#/tools/tech-persona-carousel",
    },
    {
      title: "ABM Marketing PDF",
      desc: "Coursera-branded guide + scorecard + wheel snapshot.",
      href: "#/pdf/marketing",
    },
    {
      title: "ABM 101 Video Walkthrough",
      desc: "Watch the ABM Marketing & Sales 101 guide come to life.",
      href: "https://assets.fairwaydigitalmedia.com/coursera/videos/abm_101.mp4",
    },
  ],
  sdr: [
    {
      title: "ABM SDR PDF",
      desc: "TAL quick-start: first 48 hrs, plays, worksheet.",
      href: "#/pdf/sdr",
    },
    {
      title: "SDR Video Walkthrough",
      desc: "New SDR video on TALs, signals, and booking better meetings.",
      href: "https://assets.fairwaydigitalmedia.com/coursera/videos/coursera_abm_sdr.mp4",
    },
    {
      title: "Social Comments → Contacts",
      desc: "Turn social reactions into clean contact lists (CSV/XLSX).",
      href: "#/tools/social-comments",
    },
  ],
  sales: [
    {
      title: "ABM Sales PDF",
      desc: "Account plan example, rhythms, collaboration best practices.",
      href: "#/pdf/sales",
    },
    {
      title: "Sales Account Plan Video",
      desc: "Video walkthrough of the account plan framework & examples.",
      href: "https://assets.fairwaydigitalmedia.com/coursera/videos/sales_accountplans.mp4",
    },
    {
      title: "ABM Scorecard (PDF)",
      desc: "Use the 1-pager to prep for QBRs and deal clinics.",
      href: "#/pdf/marketing",
    },
  ],
};

/* =========================
   2) Utilities
========================= */
const fmtDate = (iso) => new Date(iso).toLocaleDateString();
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const secsToMin = (s) => Math.round((s || 0) / 60);
const includesI = (h, n) => h.toLowerCase().includes((n || "").toLowerCase());
const fullTextMatch = (p, q) => {
  if (!q) return true;
  const fields = [
    p.title || "",
    p.summary || "",
    ...(p.topics || []),
    ...(p.tags || []),
    ...(p.personas || []),
    ...(p.industries || []),
  ].join(" \n ");
  return includesI(fields, q);
};
const computeYear = (iso) => {
  try {
    return String(new Date(iso).getFullYear());
  } catch {
    return "";
  }
};
const tally = (arr) => arr.reduce((m, k) => ((m[k] = (m[k] || 0) + 1), m), {});
const hashForTab = (key) =>
  key === "sdr"
    ? "#/pdf/sdr"
    : key === "sales"
    ? "#/pdf/sales"
    : "#/pdf/marketing";

/* =========================
   3) Section meta (used for hero + presets)
========================= */
const SECTIONS = [
  {
    key: "marketing",
    label: "ABM Marketing",
    presetPersonas: ["Marketing Leadership", "Demand Gen"],
    title: "ABM Marketing Enablement",
    desc: "Campaign frameworks, ABM reporting, and launch-ready content for this quarter.",
  },
  {
    key: "sdr",
    label: "ABM SDR",
    presetPersonas: ["SDR", "BDR", "Inside Sales"],
    title: "ABM SDR Enablement",
    desc: "Clear steps to work TALs, map buying committees, and book quality meetings.",
  },
  {
    key: "sales",
    label: "ABM Sales",
    presetPersonas: ["Account Executive", "Sales Leadership"],
    title: "ABM Sales Enablement",
    desc: "Account planning examples, collaboration rhythms, and templates to progress deals.",
  },
];

function useActiveSection() {
  const pick = () => {
    const raw = (window.location.hash || "").replace(/^#\/?/, "");
    const head = raw.split("/")[0];
    return SECTIONS.find((s) => s.key === head)?.key || "marketing";
  };
  const [key, setKey] = useState(pick());
  useEffect(() => {
    const onHash = () => setKey(pick());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  return key;
}

/* =========================
   4) Page Band (hero)
========================= */
function PageBand({ tabMeta }) {
  return (
    <section
      className="band"
      aria-labelledby={`band-title-${tabMeta.key}`}
      style={{
        backgroundImage: `
          url(${CONDUIT_60}),
          radial-gradient(1200px 400px at 90% -50%, rgba(255,255,255,.12), rgba(255,255,255,0) 60%),
          linear-gradient(180deg, var(--c-navy) 0%, #0b2a55 100%)
        `,
      }}
    >
      <img className="band__watermark" src={CONDUIT_RGB} alt="" aria-hidden />
      <div className="container band__inner">
        <div className="band__copy">
          <h1 id={`band-title-${tabMeta.key}`} className="page-title">
            {tabMeta.title}
          </h1>
          <p className="page-desc">{tabMeta.desc}</p>
          <div className="actions">
            <a className="btn btn--primary" href="#resources" id="newest">
              View resources
            </a>
            <a className="btn btn--secondary" href="#submit">
              Submit an asset
            </a>
          </div>
        </div>
        <div className="band__art" aria-hidden="true">
          <img className="art__c" src={CONDUIT_35} alt="" />
        </div>
      </div>
    </section>
  );
}

/* =========================
   5) Tools Panel
========================= */
function ToolsPanel({ roleKey, map }) {
  const list = map[roleKey] || [];
  if (!list.length) return null;
  return (
    <section className="tools-slab" aria-labelledby="tools-title">
      <div className="tools-head">
        <h2 id="tools-title">Tools & Shortcuts</h2>
        <p className="muted">
          Quick actions tailored for this role. Open in a new tab.
        </p>
      </div>
      <div className="tools-grid">
        {list.map((t) => (
          <a
            key={t.title}
            className="tool-card"
            href={t.href}
            target={t.href.startsWith("http") ? "_blank" : "_self"}
            rel={t.href.startsWith("http") ? "noopener noreferrer" : undefined}
          >
            <div className="tool-card__body">
              <div className="tool-card__title">{t.title}</div>
              <div className="tool-card__desc">{t.desc}</div>
            </div>
            <span className="tool-card__cta" aria-hidden>
              →
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* =========================
   6) Main (no internal header)
========================= */
export default function HubApp() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tools, setTools] = useState(DEFAULT_TOOLS);

  // filters
  const [q, setQ] = useState("");
  const [ctype, setCtype] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [topics, setTopics] = useState([]);
  const [tags, setTags] = useState([]);
  const [stage, setStage] = useState([]);
  const [year, setYear] = useState([]);
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const activeKey = useActiveSection();
  const activeMeta = useMemo(
    () => SECTIONS.find((t) => t.key === activeKey) || SECTIONS[0],
    [activeKey]
  );

  // data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        if (DATA_URL) {
          const r = await fetch(DATA_URL, { mode: "cors" });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const json = await r.json();
          const arr = Array.isArray(json) ? json : json.items || [];
          if (!cancelled) setItems(arr);
        } else {
          if (!cancelled) setItems(SAMPLE.items);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setItems(SAMPLE.items);
          setError("Using fallback sample data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => (cancelled = true);
  }, []);

  // resources map (deep-merge JSON onto DEFAULT_TOOLS)
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!RESOURCES_URL) return;
      try {
        const r = await fetch(RESOURCES_URL, { mode: "cors" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (cancel) return;

        const merged = { ...DEFAULT_TOOLS };
        if (json && typeof json === "object") {
          for (const [key, list] of Object.entries(json)) {
            if (!Array.isArray(list)) continue;
            merged[key] = [...(merged[key] || []), ...list];
          }
        }
        setTools(merged);
      } catch (e) {
        console.warn("Resources map fetch failed; using defaults.", e);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  // persona preset per section
  useEffect(() => {
    setPersonas(activeMeta.presetPersonas);
    setQ("");
    setPage(1);
  }, [activeKey]); // eslint-disable-line

  // filtering
  const filtered = useMemo(() => {
    const qTrim = q.trim();
    return items.filter((p) => {
      if (!fullTextMatch(p, qTrim)) return false;
      if (ctype.length && !ctype.includes(p.content_type)) return false;
      if (
        industries.length &&
        !industries.every((v) => (p.industries || []).includes(v))
      )
        return false;
      if (
        personas.length &&
        !personas.every((v) => (p.personas || []).includes(v))
      )
        return false;
      if (topics.length && !topics.every((v) => (p.topics || []).includes(v)))
        return false;
      if (tags.length && !tags.every((v) => (p.tags || []).includes(v)))
        return false;
      if (stage.length && !stage.includes(p.funnel_stage)) return false;
      if (year.length && !year.includes(computeYear(p.release_date)))
        return false;
      return true;
    });
  }, [items, q, ctype, industries, personas, topics, tags, stage, year]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "newest":
        arr.sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
        break;
      case "oldest":
        arr.sort((a, b) => new Date(a.release_date) - new Date(b.release_date));
        break;
      case "shortest":
        arr.sort(
          (a, b) =>
            (a.read_time_min || 0) - (b.read_time_min || 0) ||
            new Date(b.release_date) - new Date(a.release_date)
        );
        break;
      case "longest":
        arr.sort(
          (a, b) =>
            (b.read_time_min || 0) - (a.read_time_min || 0) ||
            new Date(b.release_date) - new Date(a.release_date)
        );
        break;
      default: {
        if (q.trim()) {
          const terms = q.trim().toLowerCase().split(/\s+/);
          const score = (p) => {
            const text =
              (p.title || "") +
              "\n" +
              (p.summary || "") +
              "\n" +
              (Array.isArray(p.topics) ? p.topics.join(" ") : "") +
              "\n" +
              (Array.isArray(p.tags) ? p.tags.join(" ") : "") +
              "\n" +
              (Array.isArray(p.personas) ? p.personas.join(" ") : "") +
              "\n" +
              (Array.isArray(p.industries) ? p.industries.join(" ") : "");
            const lower = text.toLowerCase();
            let s = 0;
            for (const w of terms) {
              if (!w) continue;
              if ((p.title || "").toLowerCase().includes(w)) s += 5;
              if ((p.summary || "").toLowerCase().includes(w)) s += 2;
              if (lower.includes(w)) s += 1;
            }
            const yearsOld =
              (Date.now() - new Date(p.release_date).getTime()) /
              (1000 * 60 * 60 * 24 * 365);
            s += Math.max(0, 2 - yearsOld);
            return s;
          };
          arr.sort((a, b) => score(b) - score(a));
        } else {
          arr.sort(
            (a, b) => new Date(b.release_date) - new Date(a.release_date)
          );
        }
      }
    }
    return arr;
  }, [filtered, sort, q]);

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = clamp(page, 1, pageCount);
  const pageItems = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  const facets = useMemo(() => {
    const yrs = filtered.map((p) => computeYear(p.release_date));
    const typ = filtered.map((p) => p.content_type || "content");
    const ind = filtered.flatMap((p) => p.industries || []);
    const per = filtered.flatMap((p) => p.personas || []);
    const top = filtered.flatMap((p) => p.topics || []);
    const tag = filtered.flatMap((p) => p.tags || []);
    const stg = filtered.map((p) => p.funnel_stage);
    return {
      type: tally(typ),
      year: tally(yrs),
      industries: tally(ind),
      personas: tally(per),
      topics: tally(top),
      tags: tally(tag),
      stage: tally(stg),
    };
  }, [filtered]);

  const toggle = (setter, arr, v) =>
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const Pill = ({ label, count, selected, onClick }) => (
    <button
      onClick={onClick}
      className={"pill " + (selected ? "pill--on" : "")}
      aria-pressed={selected}
    >
      {label} <span className="pill__n">({count})</span>
    </button>
  );

  const Facet = ({ title, map, selected, onToggle }) => (
    <section className="facet">
      <h3 className="facet__title">{title}</h3>
      <div className="facet__wrap">
        {Object.entries(map)
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([k, n]) => (
            <Pill
              key={k}
              label={k}
              count={n}
              selected={selected.includes(k)}
              onClick={() => onToggle(k)}
            />
          ))}
      </div>
    </section>
  );

  useEffect(() => {
    setPage(1);
  }, [q, ctype, industries, personas, topics, tags, stage, year, pageSize]);

  return (
    <div className="wrap" id="top">
      {/* NO header here — the shell owns it */}
      <PageBand tabMeta={activeMeta} />

      <main className="container">
        <header className="hdr" role="search">
          <div className="hdr__row">
            <label className="lbl">
              <span className="lbl__text">Search</span>
              <input
                className="input"
                placeholder="Titles, summaries, topics, tags…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Search titles, summaries, topics, tags"
              />
            </label>

            <label className="lbl">
              <span className="lbl__text">Sort</span>
              <select
                className="select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                aria-label="Sort results"
              >
                <option value="relevance">Relevance</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="shortest">Shortest read</option>
                <option value="longest">Longest read</option>
              </select>
            </label>

            <label className="lbl">
              <span className="lbl__text">Page size</span>
              <select
                className="select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Page size"
              >
                {[6, 12, 24, 48].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {/* Active filter chips – could be wired up later */}
          <div className="chips" role="list" aria-label="Active filters"></div>
        </header>

        <div className="slab">
          <div className="grid">
            <aside className="rail" aria-label="Filters">
              <Facet
                title="Type"
                map={facets.type || {}}
                selected={ctype}
                onToggle={(v) => toggle(setCtype, ctype, v)}
              />
              <Facet
                title="Industries"
                map={facets.industries || {}}
                selected={industries}
                onToggle={(v) => toggle(setIndustries, industries, v)}
              />
              <Facet
                title="Personas"
                map={facets.personas || {}}
                selected={personas}
                onToggle={(v) => toggle(setPersonas, personas, v)}
              />
              <Facet
                title="Topics"
                map={facets.topics || {}}
                selected={topics}
                onToggle={(v) => toggle(setTopics, topics, v)}
              />
              <Facet
                title="Tags"
                map={facets.tags || {}}
                selected={tags}
                onToggle={(v) => toggle(setTags, tags, v)}
              />
              <Facet
                title="Year"
                map={facets.year || {}}
                selected={year}
                onToggle={(v) => toggle(setYear, year, v)}
              />
              <Facet
                title="Funnel Stage"
                map={facets.stage || {}}
                selected={stage}
                onToggle={(v) => toggle(setStage, stage, v)}
              />
            </aside>

            <section
              className="main"
              id="resources"
              aria-live="polite"
              role="region"
              aria-labelledby={`band-title-${activeMeta.key}`}
            >
              {loading ? (
                <div className="muted">Loading…</div>
              ) : error ? (
                <div className="warn" role="status">
                  {error}
                </div>
              ) : null}
              <div className="muted small">{total} results</div>

              <div className="cards">
                {pageItems.map((p) => (
                  <article key={p.id} className="card">
                    {p.content_type === "video" ? (
                      <div className="media">
                        <video
                          controls
                          preload="metadata"
                          poster={p.cover_url || undefined}
                          src={p.file_url}
                          width="100%"
                        />
                      </div>
                    ) : p.cover_url ? (
                      <div className="media">
                        <img
                          className="cover"
                          src={p.cover_url}
                          alt={`Cover for ${p.title}`}
                        />
                      </div>
                    ) : null}

                    <div className="card__hdr">
                      <h3 className="card__title">{p.title}</h3>
                      <div className="card__meta">
                        {fmtDate(p.release_date)} •{" "}
                        {p.content_type === "video"
                          ? `${secsToMin(p.duration_sec)} min`
                          : `${
                              p.read_time_min ||
                              Math.round((p.words || 1200) / 200)
                            } min`}{" "}
                        • v{p.version}
                      </div>
                    </div>

                    <p className="card__sum">{p.summary}</p>

                    <div className="tags">
                      <span className="tag tag--kind">
                        {p.content_type === "playbook"
                          ? "Playbook"
                          : p.content_type === "video"
                          ? "Video"
                          : p.content_type}
                      </span>
                      {(p.topics || []).slice(0, 3).map((t) => (
                        <span key={t} className="tag">
                          {t}
                        </span>
                      ))}
                    </div>

                    <div className="cta">
                      <a
                        className="btn btn--secondary"
                        href={p.file_url || hashForTab(activeMeta.key)}
                        aria-label={`Open resource: ${p.title}`}
                      >
                        Open Resource
                      </a>
                    </div>
                  </article>
                ))}
              </div>

              {pageCount > 1 && (
                <div
                  className="pager"
                  role="navigation"
                  aria-label="Pagination"
                >
                  <button
                    className="btn btn--secondary"
                    disabled={safePage <= 1}
                    onClick={() => setPage(safePage - 1)}
                  >
                    Prev
                  </button>
                  <span className="pager__status">
                    Page {safePage} of {pageCount}
                  </span>
                  <button
                    className="btn btn--secondary"
                    disabled={safePage >= pageCount}
                    onClick={() => setPage(safePage + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </section>
          </div>
        </div>

        {/* Tools shelf for the active tab */}
        <ToolsPanel roleKey={activeMeta.key} map={tools} />
      </main>

      {/* Styles (no mastbar/app-header classes here) */}
      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap');

  :root{
    --c-blue:#2A73CC;
    --c-navy:#0E2B57;
    --ink:#0B1A3C;
    --ink70:#0B1A3CB3;
    --muted:#6B7280;
    --line:#E5E7EB;
    --bg:#F4F6FA;
    --card:#FFFFFF;

    --containerW:1180px;

    --radius-sm:8px;
    --radius-md:12px;
    --radius-lg:16px;

    --shadow-xs:0 1px 2px rgba(0,0,0,.06);
    --shadow-sm:0 2px 6px rgba(0,0,0,.08);
    --shadow-md:0 6px 20px rgba(0,0,0,.12);
  }

  *{box-sizing:border-box}
  html,body,#root{height:100%}
  body{
    margin:0; background:var(--bg); color:var(--ink);
    font-family:"Source Sans Pro",system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif;
    font-size:16px; line-height:1.5;
  }
  a{color:var(--c-blue); text-decoration:none}
  a:focus, button:focus, .select:focus, .input:focus{
    outline:2px solid color-mix(in srgb, var(--c-blue) 40%, white);
    outline-offset:2px; border-radius:6px;
  }

  /* --- Hero band --- */
  .band{
    position:relative; overflow:hidden; color:#fff;
    min-height:280px; padding:28px 0 24px;
    background-repeat:no-repeat,no-repeat,no-repeat;
    background-size:520px, cover, cover;
    background-position:108% -36px, right top, left top;
  }
  .band__watermark{
    position:absolute; left:-110px; top:-48px; width:300px;
    opacity:.15; filter:drop-shadow(0 2px 4px rgba(0,0,0,.1));
  }
  .container{max-width:var(--containerW); margin:0 auto; padding:0 16px}
  .band__inner{display:flex; align-items:flex-end; justify-content:space-between; gap:24px}
  .band__copy{max-width:720px}
  .band__art{flex:0 0 260px; min-height:120px; display:flex; align-items:flex-end; justify-content:flex-end}
  .art__c{width:160px; opacity:.9; filter:drop-shadow(0 6px 16px rgba(0,0,0,.18))}
  .page-title{margin:0; font-size:28px; line-height:1.25; font-weight:700}
  .page-desc{margin:6px 0 0; opacity:.9}
  .actions{display:flex; gap:8px; margin-top:12px}
  @media (max-width:900px){
    .band{min-height:220px; padding:22px 0}
    .band__art{display:none}
    .page-title{font-size:24px}
  }

  /* --- Main slab & layout --- */
  .slab{
    background:var(--card);
    margin-top:-12px; padding:16px;
    border-radius:16px; box-shadow:var(--shadow-md);
  }
  .hdr{margin-top:12px}
  .hdr__row{display:flex; gap:12px; align-items:end; flex-wrap:wrap; margin:8px 0}
  .lbl{display:flex; flex-direction:column; gap:6px; min-width:220px}
  .lbl__text{font-size:12px; color:var(--ink70)}
  .input{flex:1; min-width:260px; padding:10px 12px; border:1px solid var(--line); border-radius:8px; background:white}
  .select{padding:10px 12px; border:1px solid var(--line); border-radius:8px; background:white}
  .chips{display:flex; flex-wrap:wrap; gap:8px; margin-top:8px}
  .chip{
    background:color-mix(in srgb, var(--c-blue) 12%, white);
    color:#163C7A; border:1px solid color-mix(in srgb, var(--c-blue) 22%, white);
    border-radius:999px; padding:6px 10px; font-size:12px;
  }

  .grid{display:grid; grid-template-columns:280px 1fr; gap:16px; margin-top:8px}
  @media (max-width:900px){ .grid{grid-template-columns:1fr} }
  .rail{display:flex; flex-direction:column; gap:14px}
  .facet__title{margin:0 0 6px 0; font-size:14px}
  .facet__wrap{display:flex; flex-wrap:wrap; gap:8px}
  .pill{
    padding:6px 10px; border:1px solid var(--line); border-radius:999px;
    background:white; font-size:12px; color:var(--ink);
    transition:transform .15s, box-shadow .15s;
  }
  .pill:hover{transform:translateY(-1px); box-shadow:var(--shadow-sm)}
  .pill--on{background:var(--c-blue); color:white; border-color:var(--c-blue)}
  .pill__n{opacity:.7}

  /* --- Cards --- */
  .main{min-height:300px}
  .muted{color:var(--muted)}
  .small{font-size:12px; margin:8px 0}
  .cards{display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-top:8px}
  @media (max-width:1100px){ .cards{grid-template-columns:repeat(2,1fr)} }
  @media (max-width:700px){ .cards{grid-template-columns:1fr} }
  .card{
    border:1px solid var(--line); border-radius:16px; padding:12px; overflow:hidden; background:var(--card);
    box-shadow:var(--shadow-sm); transition: box-shadow .15s ease-out, transform .15s ease-out;
  }
  .card:hover{box-shadow:var(--shadow-md); transform:translateY(-1px)}
  .card:active{transform:translateY(0); box-shadow:var(--shadow-xs)}
  .media{margin:-12px -12px 8px -12px}
  .cover{width:100%; display:block; aspect-ratio:16/9; object-fit:cover}
  .card__title{margin:0; font-size:18px; line-height:24px; font-weight:700; color:var(--ink)}
  .card__meta{font-size:12px; line-height:16px; font-weight:600; color:var(--muted); margin-top:4px}
  .card__sum{font-size:15px; line-height:22px; margin:10px 0 8px; color:#172B4D}
  .tags{display:flex; gap:8px; flex-wrap:wrap}
  .tag{
    background:color-mix(in srgb, var(--c-blue) 8%, white);
    color:#163C7A; border:1px solid color-mix(in srgb, var(--c-blue) 18%, white);
    border-radius:999px; padding:4px 8px; font-size:11px;
  }
  .tag--kind{background:color-mix(in srgb, var(--c-blue) 14%, white)}
  .cta{margin-top:10px}

  /* --- Buttons --- */
  .btn{display:inline-flex; align-items:center; justify-content:center; gap:8px;
       padding:10px 14px; border-radius:8px; font-weight:600; border:1px solid transparent;
       transition:background .15s, box-shadow .15s, transform .15s}
  .btn--primary{background:var(--c-blue); color:white; border-color:var(--c-blue)}
  .btn--primary:hover{filter:brightness(.97); box-shadow:var(--shadow-sm); transform:translateY(-1px)}
  .btn--secondary{background:white; color:var(--ink); border-color:var(--line)}
  .btn--secondary:hover{background:color-mix(in srgb, white 96%, var(--c-blue) 4%); border-color:color-mix(in srgb, var(--c-blue) 25%, white)}

  /* --- Pager --- */
  .pager{display:flex; gap:10px; align-items:center; justify-content:center; margin:16px 0}
  .pager__status{color:var(--muted)}

  /* --- Tools shelf --- */
  .tools-slab{
    margin:24px 0 8px; padding:16px;
    background:var(--card); border:1px solid var(--line);
    border-radius:16px; box-shadow:var(--shadow-sm);
  }
  .tools-head{display:flex; align-items:end; justify-content:space-between; gap:12px; margin-bottom:12px}
  .tools-head h2{margin:0; font-size:18px; line-height:24px}
  .tools-grid{display:grid; grid-template-columns:repeat(3,1fr); gap:12px}
  @media (max-width:1100px){ .tools-grid{grid-template-columns:repeat(2,1fr)} }
  @media (max-width:700px){ .tools-grid{grid-template-columns:1fr} }
  .tool-card{
    display:flex; align-items:center; justify-content:space-between; gap:12px;
    padding:14px 16px; border-radius:14px; border:1px solid var(--line);
    background:#fff; color:var(--ink);
    transition:box-shadow .15s, transform .15s, border-color .15s;
  }
  .tool-card:hover{ transform:translateY(-1px); box-shadow:var(--shadow-md); border-color:color-mix(in srgb, var(--c-blue) 25%, var(--line))}
  .tool-card__title{font-weight:700}
  .tool-card__desc{color:var(--muted); font-size:14px; margin-top:2px}
  .tool-card__cta{font-size:20px; color:var(--c-blue)}
`}</style>
    </div>
  );
}
